"use client";

/**
 * First-visit currency welcome.
 *
 * Shows a small, friendly, centred card ONLY when the `ev_currency` cookie is
 * not yet set (a genuine first visit). The visitor picks Canada (CAD) or the
 * United States (USD); the choice is written through useCurrency (which sets
 * the one-year cookie and broadcasts the change to Pricing / FAQ / the header
 * switch), the card closes, and it never shows again because the cookie is now
 * present.
 *
 * Geo suggestion: middleware seeds the cookie from IP geo before the page
 * renders, so on a true first visit the cookie is normally already present and
 * this card does not appear. It appears when the cookie is genuinely absent
 * (middleware skipped, cookie cleared, or a static/edge case); in that case we
 * pre-highlight the brand default currency as the suggested choice. The small
 * dismiss (X) accepts that suggested currency without forcing an explicit
 * click.
 */

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useCurrency, CURRENCY_COOKIE, CURRENCY_CHOICE_COOKIE } from "@/lib/useCurrency";
import { DEFAULT_CURRENCY, type Currency } from "@/lib/constants/pricing-display";
import { CanadaFlag, USFlag } from "@/components/CurrencyFlags";
import { backdrop } from "@/lib/glass/style";

function choiceMade(): boolean {
  if (typeof document === "undefined") return true; // never flash on the server
  return document.cookie
    .split("; ")
    .some((row) => row.startsWith(`${CURRENCY_CHOICE_COOKIE}=`));
}

function seededCurrency(): Currency {
  if (typeof document === "undefined") return DEFAULT_CURRENCY;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${CURRENCY_COOKIE}=`));
  const value = match?.split("=")[1];
  return value === "USD" || value === "CAD" ? (value as Currency) : DEFAULT_CURRENCY;
}

export default function CurrencyWelcome() {
  const { setCurrency } = useCurrency();
  // Undefined until we check on the client; false hides, true shows. The prompt
  // appears until the visitor has EXPLICITLY chosen (or dismissed), regardless
  // of the middleware geo-seed.
  const [open, setOpen] = useState<boolean | undefined>(undefined);
  const [suggested, setSuggested] = useState<Currency>(DEFAULT_CURRENCY);

  useEffect(() => {
    setSuggested(seededCurrency());
    setOpen(!choiceMade());
  }, []);

  if (!open) return null;

  const choose = (currency: Currency) => {
    setCurrency(currency); // also marks the choice cookie
    setOpen(false);
  };

  const dismiss = () => {
    // Dismiss keeps the geo-suggested currency and records the choice so the
    // card never returns.
    setCurrency(suggested);
    setOpen(false);
  };

  const options: Array<{
    currency: Currency;
    label: string;
    code: string;
    Flag: typeof CanadaFlag;
  }> = [
    { currency: "CAD", label: "Canada", code: "CAD", Flag: CanadaFlag },
    { currency: "USD", label: "United States", code: "USD", Flag: USFlag },
  ];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="currency-welcome-title"
      onClick={dismiss}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        background: "rgba(15, 17, 23, 0.35)",
        ...backdrop("blur(4px)"),
        animation: "cw-fade 0.25s ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 420,
          background: "var(--color-bg)",
          border: "1px solid var(--color-border)",
          borderRadius: 18,
          padding: "36px 32px 32px",
          textAlign: "center",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          animation: "cw-rise 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            background: "transparent",
            border: "none",
            color: "var(--color-text-muted)",
            cursor: "pointer",
            padding: 4,
            lineHeight: 0,
            borderRadius: 8,
          }}
        >
          <X size={18} />
        </button>

        <h2
          id="currency-welcome-title"
          style={{
            fontFamily: "var(--font-heading)",
            fontSize: "1.7rem",
            color: "var(--color-text)",
            margin: "0 0 8px",
            lineHeight: 1.2,
          }}
        >
          Welcome. Where are you located?
        </h2>
        <p
          style={{
            fontFamily: "var(--font-body)",
            fontSize: "0.92rem",
            fontWeight: 300,
            color: "var(--color-text-muted)",
            margin: "0 0 24px",
            lineHeight: 1.5,
          }}
        >
          So we can show you pricing in your currency.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {options.map(({ currency, label, code, Flag }) => {
            const isSuggested = currency === suggested;
            return (
              <button
                key={currency}
                type="button"
                onClick={() => choose(currency)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  width: "100%",
                  padding: "16px 18px",
                  borderRadius: 12,
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "var(--font-body)",
                  background: isSuggested ? "var(--color-bg-alt)" : "transparent",
                  border: isSuggested
                    ? "1.5px solid var(--color-accent)"
                    : "1.5px solid var(--color-border)",
                  transition: "border-color 0.18s ease, background 0.18s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--color-accent)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = isSuggested
                    ? "var(--color-accent)"
                    : "var(--color-border)";
                }}
              >
                <Flag size={30} />
                <span style={{ display: "flex", flexDirection: "column" }}>
                  <span
                    style={{
                      fontSize: "1rem",
                      fontWeight: 500,
                      color: "var(--color-text)",
                    }}
                  >
                    {label}
                  </span>
                  <span
                    style={{
                      fontSize: "0.78rem",
                      fontWeight: 300,
                      color: "var(--color-text-muted)",
                    }}
                  >
                    Prices in {code}
                    {isSuggested ? " · Suggested for you" : ""}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes cw-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes cw-rise {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
